import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import {
  Loader2, Undo2, ShieldCheck, FileText, Mail, Clock, AlertTriangle,
  CheckCircle2, XCircle, RotateCcw, ArrowRight, Inbox, Phone, AtSign,
  Calendar as CalendarIcon, DollarSign, Sparkles, MessageCircleQuestion,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";

import {
  REFUND_STATUSES,
  listRefunds,
  refundsStats,
  verifyRefund,
  approveRefund,
  rejectRefund,
  updateRefundNotes,
  clearAdminToken,
} from "@/lib/api";

const fmtMoney = (v) =>
  v == null || Number.isNaN(Number(v))
    ? "—"
    : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return format(new Date(iso), "PP p"); } catch { return iso; }
};

const StatusPill = ({ status }) => {
  const cfg = REFUND_STATUSES.find((s) => s.id === status) || REFUND_STATUSES[0];
  return (
    <span
      className="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
      style={{ background: cfg.bg, color: cfg.color, borderColor: cfg.border }}
      data-testid={`refund-status-pill-${status}`}
    >
      {cfg.label}
    </span>
  );
};

// =============================================================================
// EmptyState
// =============================================================================
const EmptyState = ({ icon: Icon = Inbox, title, description, action }) => (
  <div
    className="rounded-2xl border bg-white p-10 sm:p-14 text-center"
    style={{ borderColor: "#E2E8F0" }}
    data-testid="refund-empty-state"
  >
    <div
      className="mx-auto mb-5 grid place-items-center h-14 w-14 rounded-2xl"
      style={{ background: "rgba(212,175,55,0.12)", color: "#7A5E12" }}
    >
      <Icon className="h-7 w-7" />
    </div>
    <div className="font-display text-xl sm:text-2xl font-semibold" style={{ color: "#0B2545" }}>
      {title}
    </div>
    {description ? (
      <p className="mt-2 text-sm max-w-md mx-auto" style={{ color: "#475569" }}>
        {description}
      </p>
    ) : null}
    {action ? <div className="mt-6">{action}</div> : null}
  </div>
);

// =============================================================================
// Detail drawer
// =============================================================================
const DetailRow = ({ label, value, mono = false, testId }) => (
  <div className="flex items-start justify-between gap-3 py-2.5 border-b last:border-b-0" style={{ borderColor: "#F1F5F9" }} data-testid={testId}>
    <span className="text-[11px] uppercase tracking-[0.18em] font-medium shrink-0" style={{ color: "#64748B" }}>{label}</span>
    <span className={`text-sm text-right ${mono ? "font-mono text-[12px]" : "font-medium"}`} style={{ color: "#0B2545" }}>{value || "—"}</span>
  </div>
);

const RefundDetailDrawer = ({ refund, onClose, onChange }) => {
  const [busy, setBusy] = useState(null);
  const [notes, setNotes] = useState(refund?.internal_notes || "");
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);

  useEffect(() => {
    setNotes(refund?.internal_notes || "");
    setShowReject(false);
    setRejectReason("");
  }, [refund?.id]);

  if (!refund) return null;

  const canVerify = refund.status === "pending_verification";
  const canApprove = refund.status === "eligible_for_refund";
  const canReject = !["refunded", "refund_processing"].includes(refund.status);

  const doAction = async (label, fn) => {
    try {
      setBusy(label);
      const updated = await fn();
      onChange?.(updated);
      toast.success(`${label} complete.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${label} failed.`);
    } finally {
      setBusy(null);
    }
  };

  const onVerify  = () => doAction("Verify",  () => verifyRefund(refund.id));
  const onApprove = () => doAction("Approve & Refund", () => approveRefund(refund.id));
  const onReject  = async () => {
    if (!rejectReason.trim()) {
      toast.error("Add a brief rejection reason.");
      return;
    }
    await doAction("Reject", () => rejectRefund(refund.id, rejectReason.trim()));
    setShowReject(false);
  };
  const onSaveNotes = () => doAction("Save notes", () => updateRefundNotes(refund.id, notes));

  return (
    <Sheet open={!!refund} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl p-0 flex flex-col"
        data-testid="refund-detail-drawer"
      >
        <SheetHeader className="px-5 py-4 border-b" style={{ borderColor: "#E2E8F0", background: "#F8FAFC" }}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <SheetTitle className="text-base font-display" style={{ color: "#0B2545" }}>
                Refund · {refund.lead_request_id || refund.id.slice(0, 8)}
              </SheetTitle>
              <SheetDescription className="text-xs" style={{ color: "#64748B" }}>
                Submitted {fmtDate(refund.created_at)} · {refund.customer_email}
              </SheetDescription>
            </div>
            <StatusPill status={refund.status} />
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {refund.verification_message ? (
            <div
              className="rounded-lg border p-3 text-sm"
              style={{
                borderColor: refund.status === "not_eligible" || refund.status === "rejected" ? "rgba(220,38,38,0.3)" : "rgba(212,175,55,0.35)",
                background:  refund.status === "not_eligible" || refund.status === "rejected" ? "rgba(220,38,38,0.06)" : "rgba(212,175,55,0.06)",
                color: refund.status === "not_eligible" || refund.status === "rejected" ? "#991B1B" : "#7A5E12",
              }}
              data-testid="refund-verification-banner"
            >
              <div className="flex items-start gap-2">
                {refund.status === "not_eligible" || refund.status === "rejected"
                  ? <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  : <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />}
                <span>{refund.verification_message}</span>
              </div>
            </div>
          ) : null}

          {/* Refund details */}
          <section className="rounded-xl border bg-white" style={{ borderColor: "#E2E8F0" }}>
            <div className="px-4 py-2 border-b text-[11px] uppercase tracking-[0.18em] font-medium" style={{ borderColor: "#E2E8F0", color: "#475569" }}>
              Refund details
            </div>
            <div className="px-4 py-2">
              <DetailRow testId="d-lead-id" label="Lead ID" value={refund.lead_request_id || refund.lead_id} mono />
              <DetailRow testId="d-invoice" label="Invoice number" value={refund.invoice_number} mono />
              <DetailRow testId="d-customer" label="Customer name" value={refund.customer_name} />
              <DetailRow testId="d-email" label="Customer email" value={refund.customer_email} />
              <DetailRow testId="d-service" label="Service type" value={refund.service_type} />
              <DetailRow testId="d-urgency" label="Service urgency" value={refund.urgency} />
              <DetailRow testId="d-amount" label="Original payment amount" value={fmtMoney(refund.original_amount)} />
              <DetailRow testId="d-paydate" label="Payment date" value={fmtDate(refund.payment_date)} />
              <DetailRow testId="d-refund-status" label="Refund status" value={
                <StatusPill status={refund.status} />
              } />
              <DetailRow testId="d-stripe-refund" label="Stripe refund ID" value={refund.stripe_refund_id} mono />
              {refund.reason ? (
                <DetailRow testId="d-reason" label="Customer reason" value={refund.reason} />
              ) : null}
            </div>
          </section>

          {/* Internal notes */}
          <section className="rounded-xl border bg-white" style={{ borderColor: "#E2E8F0" }}>
            <div className="px-4 py-2 border-b text-[11px] uppercase tracking-[0.18em] font-medium" style={{ borderColor: "#E2E8F0", color: "#475569" }}>
              Internal notes
            </div>
            <div className="px-4 py-3">
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add context — verification steps, customer correspondence, decisions…"
                className="min-h-[100px] text-sm"
                data-testid="refund-internal-notes"
              />
              <div className="mt-2 flex justify-end">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={onSaveNotes}
                  disabled={busy === "Save notes"}
                  className="h-8 text-xs"
                  data-testid="refund-save-notes"
                >
                  {busy === "Save notes" ? <Loader2 className="h-3 w-3 animate-spin mr-1.5" /> : null}
                  Save notes
                </Button>
              </div>
            </div>
          </section>

          {/* Audit log */}
          {Array.isArray(refund.audit_log) && refund.audit_log.length > 0 ? (
            <section className="rounded-xl border bg-white" style={{ borderColor: "#E2E8F0" }} data-testid="refund-audit-log">
              <div className="px-4 py-2 border-b text-[11px] uppercase tracking-[0.18em] font-medium" style={{ borderColor: "#E2E8F0", color: "#475569" }}>
                Audit log
              </div>
              <ul className="divide-y" style={{ borderColor: "#E2E8F0" }}>
                {[...refund.audit_log].reverse().map((entry, i) => (
                  <li key={i} className="px-4 py-2.5 flex items-start gap-2.5">
                    <span className="mt-1 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: "#0B2545" }} />
                    <div className="min-w-0 flex-1">
                      <div className="text-[12px] leading-snug" style={{ color: "#0B2545" }}>{entry.summary}</div>
                      <div className="text-[10px]" style={{ color: "#94A3B8" }}>
                        {fmtDate(entry.ts)} · {entry.actor || "system"}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>

        {/* Sticky footer actions */}
        <div className="border-t px-5 py-3 bg-white" style={{ borderColor: "#E2E8F0" }}>
          {showReject ? (
            <div className="space-y-2">
              <Input
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Reason for rejection (required)…"
                className="h-9 text-sm"
                data-testid="refund-reject-reason"
              />
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="h-9 flex-1" onClick={() => setShowReject(false)} data-testid="refund-cancel-reject">
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="h-9 flex-1"
                  onClick={onReject}
                  disabled={busy === "Reject" || !rejectReason.trim()}
                  style={{ background: "#991B1B", color: "#FFFFFF" }}
                  data-testid="refund-confirm-reject"
                >
                  {busy === "Reject" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <XCircle className="h-3.5 w-3.5 mr-1.5" />}
                  Confirm reject
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {canVerify ? (
                <Button
                  size="sm" className="h-9 flex-1 min-w-[140px]" onClick={onVerify}
                  disabled={busy === "Verify"}
                  style={{ background: "#0B2545", color: "#FFFFFF" }}
                  data-testid="refund-verify"
                >
                  {busy === "Verify" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />}
                  Verify Purchase
                </Button>
              ) : null}
              {canApprove ? (
                <Button
                  size="sm" className="h-9 flex-1 min-w-[160px]" onClick={onApprove}
                  disabled={busy === "Approve & Refund"}
                  style={{ background: "#0F6D5D", color: "#FFFFFF" }}
                  data-testid="refund-approve"
                >
                  {busy === "Approve & Refund" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
                  Approve Refund
                </Button>
              ) : null}
              {canReject ? (
                <Button
                  size="sm" variant="outline" className="h-9 flex-1 min-w-[120px]"
                  onClick={() => setShowReject(true)}
                  style={{ borderColor: "#991B1B", color: "#991B1B" }}
                  data-testid="refund-open-reject"
                >
                  <XCircle className="h-3.5 w-3.5 mr-1.5" />
                  Reject
                </Button>
              ) : null}
              {refund.status === "refunded" ? (
                <a
                  className="inline-flex items-center justify-center h-9 px-3 rounded-md border text-[11px] font-semibold flex-1 min-w-[120px]"
                  style={{ borderColor: "#065F46", color: "#065F46", background: "rgba(16,185,129,0.06)" }}
                  href={`mailto:${refund.customer_email}`}
                  data-testid="refund-email-customer"
                >
                  <Mail className="h-3.5 w-3.5 mr-1.5" /> Email customer
                </a>
              ) : null}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

// =============================================================================
// Refund card (mobile + Kanban)
// =============================================================================
const RefundCard = ({ refund, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(refund)}
    className="w-full text-left rounded-lg border bg-white p-3 hover:shadow-sm transition"
    style={{ borderColor: "#E2E8F0" }}
    data-testid={`refund-card-${refund.id}`}
  >
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="text-[13px] font-semibold truncate" style={{ color: "#0B2545" }}>
          {refund.customer_name || refund.customer_email}
        </div>
        <div className="text-[11px] truncate" style={{ color: "#64748B" }}>
          {refund.lead_request_id || "—"} · {refund.invoice_number}
        </div>
      </div>
      <span className="text-[12px] font-semibold shrink-0" style={{ color: "#0B2545" }}>
        {fmtMoney(refund.original_amount)}
      </span>
    </div>
    <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
      <div className="flex items-center gap-1 text-[10px]" style={{ color: "#94A3B8" }}>
        <Clock className="h-3 w-3" />
        {fmtDate(refund.created_at)}
      </div>
      {refund.urgency ? (
        <span
          className="text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5"
          style={{
            background: refund.urgency === "Immediate" || refund.urgency === "Same Day"
              ? "rgba(220,38,38,0.1)" : "rgba(11,37,69,0.06)",
            color: refund.urgency === "Immediate" || refund.urgency === "Same Day"
              ? "#991B1B" : "#0B2545",
          }}
        >
          {refund.urgency}
        </span>
      ) : null}
    </div>
  </button>
);

// =============================================================================
// Main panel
// =============================================================================
export default function RefundCenterPanel({ onSessionExpired }) {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [query, setQuery] = useState("");

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [list, st] = await Promise.all([listRefunds(), refundsStats()]);
      setItems(list);
      setStats(st);
    } catch (e) {
      if (e?.response?.status === 401) {
        clearAdminToken();
        onSessionExpired?.();
        toast.error("Your session expired. Please sign in again.");
        return;
      }
      toast.error("Failed to load refunds.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleChange = (updated) => {
    setItems((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    setActive(updated);
    refundsStats().then(setStats).catch(() => {});
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((r) =>
      [r.customer_email, r.customer_name, r.lead_request_id, r.invoice_number, r.service_type]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [items, query]);

  const byStatus = useMemo(() => {
    const m = {};
    REFUND_STATUSES.forEach((s) => { m[s.id] = []; });
    filtered.forEach((r) => {
      if (m[r.status]) m[r.status].push(r);
      else if (r.status === "not_eligible") (m.rejected ||= []).push(r); // bucket not_eligible alongside rejected
    });
    return m;
  }, [filtered]);

  const totalCount = items.length;

  // -------------------------------- empty state -------------------------------
  if (!loading && totalCount === 0) {
    return (
      <div className="space-y-6" data-testid="refund-center-empty">
        <Header stats={stats} />
        <EmptyState
          icon={Inbox}
          title="No refund requests yet."
          description="When customers submit a refund request via the public form, you'll see them appear here, sorted by status."
          action={
            <a
              href="/refund-request"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-semibold underline"
              style={{ color: "#0B2545" }}
              data-testid="refund-open-public-form"
            >
              Open public refund form <ArrowRight className="h-3.5 w-3.5" />
            </a>
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Header stats={stats} />

      <div className="flex flex-wrap items-center gap-3">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, email, lead ID, invoice…"
          className="h-10 max-w-md"
          data-testid="refund-search"
        />
      </div>

      {loading ? (
        <div className="rounded-2xl border bg-white py-20 flex items-center justify-center gap-3"
          style={{ borderColor: "#E2E8F0", color: "#475569" }}>
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Loading refunds…</span>
        </div>
      ) : (
        <>
          {/* DESKTOP — 5-column Kanban */}
          <div className="hidden lg:grid lg:grid-cols-5 gap-4" data-testid="refund-kanban">
            {REFUND_STATUSES.map((col) => {
              const cards = byStatus[col.id] || [];
              return (
                <div
                  key={col.id}
                  className="rounded-xl border min-h-[300px] flex flex-col"
                  style={{ borderColor: col.border, background: col.bg }}
                  data-testid={`refund-column-${col.id}`}
                >
                  <div className="px-3 py-2.5 flex items-center justify-between border-b" style={{ borderColor: col.border }}>
                    <span className="text-[11px] uppercase tracking-[0.18em] font-semibold" style={{ color: col.color }}>
                      {col.label}
                    </span>
                    <span className="text-[10px] font-bold rounded-full px-2 py-0.5"
                      style={{ background: "rgba(255,255,255,0.7)", color: col.color }}>
                      {cards.length}
                    </span>
                  </div>
                  <div className="p-2 space-y-2 flex-1 overflow-y-auto max-h-[60vh]">
                    {cards.length === 0 ? (
                      <div className="text-[11px] text-center py-8" style={{ color: col.color, opacity: 0.6 }}>
                        Empty
                      </div>
                    ) : (
                      cards.map((r) => <RefundCard key={r.id} refund={r} onOpen={setActive} />)
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* MOBILE / TABLET — stacked grouped lists */}
          <div className="lg:hidden space-y-4" data-testid="refund-stacked">
            {REFUND_STATUSES.map((col) => {
              const cards = byStatus[col.id] || [];
              return (
                <section
                  key={col.id}
                  className="rounded-xl border bg-white"
                  style={{ borderColor: "#E2E8F0" }}
                  data-testid={`refund-mobile-section-${col.id}`}
                >
                  <div
                    className="px-3 py-2 flex items-center justify-between border-b"
                    style={{ borderColor: "#E2E8F0", background: col.bg }}
                  >
                    <span className="text-[11px] uppercase tracking-[0.18em] font-semibold" style={{ color: col.color }}>
                      {col.label}
                    </span>
                    <span className="text-[10px] font-bold rounded-full px-2 py-0.5"
                      style={{ background: "rgba(255,255,255,0.85)", color: col.color }}>
                      {cards.length}
                    </span>
                  </div>
                  <div className="p-2 space-y-2">
                    {cards.length === 0 ? (
                      <div className="text-[11px] text-center py-4" style={{ color: "#94A3B8" }}>
                        No refunds in this status.
                      </div>
                    ) : (
                      cards.map((r) => <RefundCard key={r.id} refund={r} onOpen={setActive} />)
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        </>
      )}

      <RefundDetailDrawer
        refund={active}
        onClose={() => setActive(null)}
        onChange={handleChange}
      />
    </div>
  );
}

// =============================================================================
// Header
// =============================================================================
const Header = ({ stats }) => (
  <>
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.22em] font-medium" style={{ color: "#7A5E12" }}>
          Refund Center
        </div>
        <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold" style={{ color: "#0B2545" }} data-testid="refund-page-title">
          Refunds &amp; cancellations
        </h1>
        <p className="mt-1 text-sm max-w-xl" style={{ color: "#475569" }}>
          Verify customer refund requests against Stripe payments, apply policy automatically, and issue one-click refunds.
        </p>
      </div>
      <a
        href="/refund-request"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 text-xs font-semibold rounded-md border px-3 py-1.5"
        style={{ borderColor: "#0B2545", color: "#0B2545", background: "#FFFFFF" }}
        data-testid="refund-open-public-form-top"
      >
        <MessageCircleQuestion className="h-3.5 w-3.5" />
        Open public refund form
      </a>
    </div>

    {/* Status totals */}
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
      {REFUND_STATUSES.map((s) => (
        <div
          key={s.id}
          className="rounded-xl border bg-white px-3 py-2.5"
          style={{ borderColor: "#E2E8F0" }}
          data-testid={`refund-stat-${s.id}`}
        >
          <div className="text-[9px] uppercase tracking-[0.18em] font-medium" style={{ color: s.color }}>{s.label}</div>
          <div className="mt-1 font-display text-2xl font-semibold" style={{ color: "#0B2545" }}>
            {stats?.[s.id] ?? 0}
          </div>
        </div>
      ))}
    </div>
  </>
);
